import React, { useEffect, useState } from 'react'
import AddEditForm from '../../../sharedComponents/AddEditForm'
import { TrashIcon, PencilIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageBanks from './useMangeBanks';
import { formatAmount } from '../../../utils/formatAmount';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '500px',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const registerItems = [
  {
    title: 'Bank Code',
    name: 'bankCode',
    type: 'text',
  },
  {
    title: 'Bank Name (TH)',
    name: 'bankNameTh',
    type: 'text',
  },
  {
    title: 'Bank Name (EN)',
    name: 'bankNameEn',
    type: 'text',
  },
  {
    title: 'Account No.',
    name: 'accountNo',
    type: 'text',
  },
  {
    title: 'Account Name (TH)',
    name: 'accountNameTh',
    type: 'text',
  },
  {
    title: 'Account Name (EN)',
    name: 'accountNameEn',
    type: 'text',
  },
  {
    title: 'Group',
    name: 'group',
    type: 'select',
    options:[
      {
        label: '> 5000',
        value: 'large'
      },
      {
        label: '< 5000',
        value: 'small'
      }
    ]
  }
];

const editItems = [
  ...registerItems,
  {
    title: 'Is Active',
    name: 'isActive',
    type: 'boolean',
  }
];

const initialValue = {
  bankCode: '',
  bankNameTh: '',
  bankNameEn: '',
  accountNo: '',
  accountNameTh: '',
  accountNameEn: '',
  group: '',
  isActive: false
}
const BankSetting = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [bank, setBank] = useState(initialValue);
  const [groupFilter, setGroupFilter] = useState('all');

  const {
    banks,
    isLoading,
    error,
    addBank,
    deleteBank,
    refreshBanks,
    updateBank
  } = useManageBanks();

  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setBank(prevBank => ({
        ...prevBank,
        [name]: type === 'checkbox' ? checked : value
    }));
  }

  const openForm = async (isEdit, id) => {
    if (isEdit) {
      const bankToEdit = banks.find(w => w.uuid === id);
      if (bankToEdit) {
        setBank(bankToEdit);
      }
    } else {
      setBank(initialValue);
    }
    setIsFormOpen(true);
  };

  const closeForm = () =>{
    setIsFormOpen(false);
  }

  const openConfirm = async (id) => {
    const bankToDelete = banks.find(w => w.uuid === id);
    if(bankToDelete) {
      setBank(bankToDelete);
      setIsConfirmOpen(true)
    }
  };

  const closeConfirm = () =>{
    setIsConfirmOpen(false)
  }

  const handleRegisterBank = async () => {
    try {
      const returnResult = await addBank(bank);
      if (returnResult) {
        toast.success('Bank registered successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      // Handle error
    }
  };

  const handleUpdateBank = async () => {
    try {
      const returnResult = await updateBank(bank);
      if (returnResult) {
        toast.success('Bank updated successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      // Handle error
    }
  };

  const handleFilterChange = (e) => {
    setGroupFilter(e.target.value);
  };

  const filteredBanks = banks.filter(bank => {
    if (groupFilter === 'all') return true;
    return bank.group === groupFilter;
  });

  useEffect(() => {
    toast.error(error);
  }, [error]);

  const handleDeleteBank = async (id) => {
    try {
      await deleteBank(id);
      toast.success('Bank deleted successfully!');
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    refreshBanks();
  }, []);

  return (
    <div>
      <h2 className='text-5xl'>Bank Setting</h2>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className='flex justify-between items-center mb-4'>
                <div className="flex items-center space-x-4 bg-white py-2 px-3 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center space-x-2">
                    <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                    </svg>
                    <label className="text-sm font-medium text-gray-700">Filter by Group:</label>
                  </div>
                  <select
                    value={groupFilter}
                    onChange={handleFilterChange}
                    className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm py-1 px-2 bg-white hover:border-gray-400 transition-colors duration-200"
                  >
                    <option value="all">All Groups</option>
                    <option value="large">&gt; 5000</option>
                    <option value="small">&lt; 5000</option>
                  </select>
                </div>
                <button 
                  onClick={()=> openForm(false, null)} 
                  className='bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded shadow-sm hover:shadow-md transition-all duration-200 flex items-center space-x-2'
                >
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  <span>Add New Bank</span>
                </button>
              </div>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">No</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Bank Code</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Bank Name (TH)</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Bank Name (EN)</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Account No.</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Account Name (TH)</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Account Name (EN)</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Group</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Transaction Count</th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredBanks.map((bank, index) => {
                      const {uuid, bankCode, bankNameTh, bankNameEn, accountNo, accountNameTh, accountNameEn, group, totalAmount, transactionCount, isActive} = bank;
                      return (
                        <tr key={uuid}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {index + 1}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{bankCode}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{bankNameTh}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{bankNameEn}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{accountNo}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{accountNameTh}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{accountNameEn}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              group === 'large' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {group === 'large' ? '> 5000' : '< 5000'}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatAmount(totalAmount)}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatAmount(transactionCount)}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {isActive && (
                              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center space-x-3">
                              <PencilIcon 
                                onClick={() => openForm(true, uuid)} 
                                className='cursor-pointer flex items-center w-6 h-6 fill-green-600'
                              />
                              <TrashIcon 
                                onClick={()=> openConfirm(uuid)} 
                                className='cursor-pointer flex items-center w-6 h-6 fill-red-600'
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm 
          items={bank.uuid ? editItems : registerItems} 
          name={bank.uuid ? 'Edit Bank' : 'New Bank Registration'} 
          data={bank} 
          mode={bank.uuid ? 'edit' : 'register'}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleUpdate={handleUpdateBank}
          handleRegister={handleRegisterBank}
          stateValue={bank}
        />
      </Modal>

      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          items={editItems} 
          data={bank} 
          closeConfirm={closeConfirm}
          confirmFunction={handleDeleteBank}
        />
      </Modal>
    </div>
  );
};

export default BankSetting;